//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useNavigate } from 'react-router-dom';
import Logoutimage from '../../icons/logout.png';
import Logo from '../../icons/devops_loop_white.svg';
import { logoutStyles } from '../../styles/logoutStyles';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { RETURN_URL_KEY } from '../../oidcConnect';
import { useConfig } from '../../Config';
import {
  isSafeRedirectUrl,
  mapProductPathToSolutionRoute,
} from '../../utils/validateUrlUtils';

function LoggedOut() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { baseName, solutions } = useConfig();

  const handleSignIn = () => {
    let returnPath = '/';
    try {
      const savedUrl = window.sessionStorage.getItem(RETURN_URL_KEY);
      window.sessionStorage.removeItem(RETURN_URL_KEY);
      if (savedUrl && isSafeRedirectUrl(savedUrl)) {
        // Extract the path relative to baseName so the login flow can
        // reconstruct the full redirect_uri for Keycloak.
        const url = new URL(savedUrl, window.location.origin);
        const fullPath = `${url.pathname}${url.search}${url.hash}`;
        returnPath =
          baseName && fullPath.startsWith(baseName)
            ? fullPath.slice(baseName.length) || '/'
            : fullPath;

        // SwitcherIframe syncs the browser URL to the iframe's internal URL
        // (e.g. /plan/#/ccmweb/welcome) via history.replaceState. That URL
        // uses the product's ingress path, not the React Router route
        // (/solution/plan). Map it back so the post-login redirect resolves
        // to a valid route instead of hitting the 404 catch-all.
        returnPath = mapProductPathToSolutionRoute(returnPath, solutions);
      }
    } catch {
      // sessionStorage may be unavailable
    }
    navigate(`/login?redirectLocation=${encodeURIComponent(returnPath)}`);
  };

  //Note: See the OIDCHelper function in src/oidcConnect.tsx
  //This is a workaround to a Keycloak issue where the KC_RESTART cookie is lost
  //when a signoutRedirect is called when the user is not logged in with a redirect URI here.
  auth.removeUser();

  return (
    <div
      id="container"
      data-testid="logged-out-container"
      style={logoutStyles.container}
    >
      <div
        id="leftsection"
        data-testid="logged-out-left-section"
        style={logoutStyles.leftsection}
      >
        <div
          id="content"
          data-testid="logged-out-content"
          style={logoutStyles.content}
        >
          <img
            src={Logoutimage}
            alt="Logged Out Icon"
            id="logouticon"
            style={logoutStyles.logoutIcon}
          />
          <div
            id="heading"
            data-testid="logged-out-heading"
            style={logoutStyles.heading}
          >
            {t('LogoutInfo')}
          </div>
          <p id="para" style={logoutStyles.paragraph}>
            {t('LogoutDescription')}
          </p>
          <button
            id="loginbutton"
            style={logoutStyles.loginButton}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                logoutStyles.loginButtonHover.backgroundColor)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                logoutStyles.loginButton.backgroundColor)
            }
            onClick={handleSignIn}
          >
            {t('Login')}
          </button>
        </div>
      </div>
      <div
        id="rightsection"
        data-testid="logged-out-right-section"
        style={logoutStyles.rightsection}
      >
        <img src={Logo} alt="Logo" id="logo" style={logoutStyles.logo} />
      </div>
    </div>
  );
}

export default LoggedOut;
